import { gql } from "@apollo/client";
import { useQuery } from "@apollo/client/react";
import { Link, useNavigate, useParams } from "react-router";
import styled from "styled-components";

import ListGeometryTypes from "./ListGeometryTypes.graphql";
import { TAXONOMY_BASE } from "../../../../../constants";
import * as UI from "../../../../../modules/ui-kit";
import { CssTruncate } from "../../../components/styles";
import { confirmUnsavedChanges } from "../../../modules/unsaved-changes";

const StyledContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
    overflow: hidden;

    .header-row {
        align-items: center;
        display: flex;
        justify-content: space-between;

        h3 {
            color: #172b4d;
            font-size: 0.95rem;
            font-weight: 700;
            margin: 0;
            text-transform: uppercase;
            letter-spacing: 0.02em;
        }
    }

    ul {
        display: flex;
        flex-direction: column;
        gap: 6px;
        list-style: none;
        margin: 0;
        overflow-y: auto;
        padding: 0;
    }
`;

/* Selection wins over the deprecated styling, and the two combine. Colours match
 * the Versions list so "selected" reads the same across the screen. */
const StyledGeometryType = styled(Link)<{ $deprecated?: boolean, $selected?: boolean }>`
    align-items: center;
    background: ${ ({ $deprecated, $selected }) => $selected ? "#deebff" : ($deprecated ? "#fafbfc" : "#fff") };
    border: 1px solid ${ ({ $selected }) => $selected ? "#0747a6" : "#ebecf0" };
    border-radius: 6px;
    display: flex;
    gap: 8px;
    justify-content: space-between;
    padding: 8px 12px;
    text-decoration: none;
    transition: border-color 0.15s, background 0.15s;

    &:hover {
        background: ${ ({ $selected }) => $selected ? "#deebff" : "#f4f5f7" };
        border-color: ${ ({ $selected }) => $selected ? "#0747a6" : "#dfe1e6" };
    }

    span {
        color: ${ ({ $deprecated, $selected }) => $selected ? "#0747a6" : ($deprecated ? "#6b778c" : "#172b4d") };
        font-size: 0.85rem;
        font-weight: ${ ({ $selected }) => $selected ? "600" : "500" };
        text-decoration: ${ ({ $deprecated }) => $deprecated ? "line-through" : "none" };

        ${ CssTruncate }
    }

    i {
        background: #ffebe6;
        border-radius: 3px;
        color: #bf2600;
        flex: none;
        font-size: 0.7rem;
        font-style: normal;
        font-weight: 600;
        padding: 1px 5px;
        text-transform: uppercase;
    }
`;

export default function GeometryTypes(props) {
    const { id, guard } = props;
    const navigate = useNavigate();

    // From the route, not a prop: this list is mounted on both the BimClass
    // screen (nothing selected) and the Geometry Type screen.
    const { geometryTypeID } = useParams();

    const listGeometryTypes = useQuery(
        gql(ListGeometryTypes),
        {
            variables: {
                id,
            },
        },
    );

    async function open(event, url) {
        if (!guard) return;
        event.preventDefault();
        if (await confirmUnsavedChanges(guard)) navigate(url);
    }

    if (listGeometryTypes.loading) return "loading";
    if (listGeometryTypes.error) return "error";

    const allTypes = listGeometryTypes.data?.geometryTypes ?? [];

    return (
        <StyledContainer>
            <div className="header-row">
                <h3>Geometry Types ({ allTypes.length })</h3>
                <UI.Button
                    $size="small"
                    as={ Link }
                    onClick={ (e) => open(e, `${ TAXONOMY_BASE }/BimClasses/${ id }/GeometryTypes/new/`) }
                    to={ `${ TAXONOMY_BASE }/BimClasses/${ id }/GeometryTypes/new/` }
                >
                    New GT
                </UI.Button>
            </div>

            <ul>
                {
                    allTypes.map(
                        (geometryType) => (
                            <li key={ geometryType.id }>
                                <StyledGeometryType
                                    $deprecated={ geometryType.deprecated }
                                    $selected={ geometryType.id === geometryTypeID }
                                    onClick={ (e) => open(e, `${ TAXONOMY_BASE }/BimClasses/${ id }/GeometryTypes/${ geometryType.id }`) }
                                    to={ `${ TAXONOMY_BASE }/BimClasses/${ id }/GeometryTypes/${ geometryType.id }` }
                                >
                                    <span title={ geometryType.name[0]?.text ?? "Untitled" }>
                                        { geometryType.name[0]?.text ?? "Untitled" }
                                    </span>
                                    { geometryType.deprecated && <i>Deprecated</i> }
                                </StyledGeometryType>
                            </li>
                        )
                    )
                }
            </ul>
        </StyledContainer>
    );
}
